import {
  type SessionsTour,
  useLazyGetSessionsToursQuery,
} from '@carboncopyco/data';
import { RightOutlineIcon } from '@carboncopyco/icons';
import { Button, TextLargeParagraph } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
  useDashboardAnalytics,
} from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useEffect } from 'react';
import { FixedTable } from '../../../FixedTable';
import { TitleCard } from '../../../InfoPage/InfoPageCards/TitleCard';
import { AssetTableSkeleton } from '../../AssetTableSkeleton';
import { useDashboardProps } from '../../DashboardContext';
import { RowWrapper } from '../../RowWrapper';
import {
  TableEmptyState,
  TableErrorState,
} from '../../SessionBreakdownComponents';
import { ViewAllToursModal } from './ViewAllToursModal';
import { getCols } from './cols';

const cols = getCols();

export const ToursTable = () => {
  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    goToTour,
  } = useDashboardProps();
  const [getSessionTours, { data, isLoading, error }] =
    useLazyGetSessionsToursQuery();
  const [showModal, setShowModal] = useBoolean();
  const { trackDashboardViewMoreClick } = useDashboardAnalytics();

  useEffect(() => {
    getSessionTours({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['tour'],
    });
  }, [
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    getSessionTours,
  ]);

  const items = data?.items ?? [];

  return (
    <>
      <TitleCard
        border="ds-regular"
        header={
          <div className="pl-6 gap-2 h-ds-15 flex items-center">
            <TextLargeParagraph
              label="Tours"
              color="text-ds-natural-600"
            />
            {!!data?.metadata.totalItems && (
              <TextLargeParagraph
                label={`(${data.metadata.totalItems})`}
                color="text-ds-natural-300"
              />
            )}
          </div>
        }
      >
        {error ? (
          <div className="p-28">
            <TableErrorState />
          </div>
        ) : items?.length || isLoading ? (
          <div className="flex flex-col pt-4.5 h-full">
            <FixedTable<SessionsTour>
              sideSpacing
              cols={cols}
              data={data?.items.slice(0, 5) ?? []}
              isLoading={isLoading}
              loader={<AssetTableSkeleton />}
              rowWrapper={({ children, item }) => {
                const separator = item !== items[items.length - 1];
                return (
                  <RowWrapper
                    onRowClick={
                      item.asset.status === 'deleted' ||
                      item.asset.status === 'inactive'
                        ? undefined
                        : () =>
                            goToTour(
                              Number(item?.asset.assetId),
                              item?.asset.workspaceId
                            )
                    }
                    size="lg"
                    separator={separator}
                  >
                    {children}
                  </RowWrapper>
                );
              }}
              loaderPlaceholderSpace={0}
            />
            {!isLoading && items.length > 5 && (
              <div className="flex items-center pl-6 py-4 h-full [&>button]:p-0">
                <Button
                  blue
                  link
                  label="View all"
                  rightIcon={<RightOutlineIcon />}
                  onClick={() => {
                    setShowModal.setTrue();
                    trackDashboardViewMoreClick({
                      dashboardSection: 'tours_table',
                    });
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="py-20">
            <TableEmptyState />
          </div>
        )}
      </TitleCard>
      {showModal && (
        <ViewAllToursModal
          open={showModal}
          onClose={setShowModal.setFalse}
        />
      )}
    </>
  );
};
